import LockPersonRoundedIcon from '@mui/icons-material/LockPersonRounded';
import { Box, Button, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

import { PageContainer, useDocumentTitle } from '@/shared/ui';

/**
 * What an account with no sections at all sees.
 *
 * <p>This is a real state, not a defensive branch: a merchant's newly invited employee belongs to no
 * department yet, so they hold no domain grants and every business area is closed to them. Before this
 * page they signed in successfully and landed on a portal with an empty sidebar and a dashboard full of
 * numbers they had no right to — which reads as "the product is broken", not as "your access has not
 * been set up". Saying so plainly, and naming who can fix it, turns a dead end into one instruction.
 *
 * <p>The link to one's own access is deliberately the action: `/team/my-access` sits outside every
 * permission guard, so it is the one screen that always works and it answers the question this page
 * raises.
 */
export function NoAccessPage() {
  const { t } = useTranslation('common');
  useDocumentTitle(t('noAccess.title'));

  return (
    <PageContainer title={t('noAccess.title')} maxWidth={720}>
      <Box
        role="status"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          gap: 1.5,
          py: 6,
          px: 3,
        }}
      >
        <LockPersonRoundedIcon sx={{ fontSize: 56, color: 'text.disabled' }} aria-hidden />
        <Typography variant="h6">{t('noAccess.heading')}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 460 }}>
          {t('noAccess.message')}
        </Typography>
        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
          <Button variant="contained" component={RouterLink} to="/team/my-access">
            {t('accessRestricted.viewMyAccess')}
          </Button>
        </Stack>
      </Box>
    </PageContainer>
  );
}
